import { createContext, useContext } from 'react';
import { FilterSaveState, ITaskCustomView, ITaskViewLayoutState, TaskViewEditableFields } from './TaskViewTypes';

export interface ICreateTaskViewInput {
  name: string;
  filterValues: string[];
  sourceRoute: string;
  parentViewId?: number | null;
  // A local view-creating-* draft this view replaces once the API has assigned its id.
  replaceDraftKey?: string;
}

// What POST taskviews/copy starts from: a view with an API id (own or shared;
// filterValues and sourceRoute default to the source's) or a built-in
// template, which has no row, so its label, filters and route travel along.
export type CopyTaskViewSource =
  | { viewKey: string; filterValues?: string[]; sourceRoute?: string }
  | { templateLabel: string; filterValues: string[]; sourceRoute: string };

// The task-view state contract shared between the scene-level provider
// (scenes/Tasks/TaskViews/TaskViewStateProvider) and the components that
// consume it. Lives in components so that components never import from
// scenes; only the provider implementation is scene-level.
export interface ITaskViewStateContextValue {
  scopeKey: string;
  // True until the first task-views + layout load settles for the current scope.
  isLayoutLoading: boolean;
  layoutSaveState: FilterSaveState;
  customViews: ITaskCustomView[];
  viewOrder: string[];
  hiddenTemplateViewKeys: string[];
  hiddenCustomViewKeys: string[];
  hiddenSidebarViewKeys: string[];
  selectedTaskViewKey: string | undefined;
  viewMemberFilters: Record<string, string[]>;
  standardViewFilterOverrides: Record<string, string[]>;
  // Save chip per view key, plus the layout chip under 'layout'. 'saved' and
  // 'error' clear themselves; no key means idle.
  viewSaveStates: Record<string, FilterSaveState>;
  // The provider is the only writer of view definitions. An edit shows at once
  // and is saved after a short delay; a draft stays local.
  editView: (viewKey: string, fields: TaskViewEditableFields) => void;
  // Saves a pending edit now (rename). Rejects only with the duplicate-name
  // AppError, after the old label is back; the provider shows every other failure.
  flushView: (viewKey: string) => Promise<void>;
  retryView: (viewKey: string) => void;
  // Undefined when the tenant/user scope changed while the POST was out; the
  // row then belongs to the previous scope and is not adopted here.
  createView: (input: ICreateTaskViewInput) => Promise<ITaskCustomView | undefined>;
  // Server-side copy: the API picks the first free "Name (n)".
  copyView: (source: CopyTaskViewSource) => Promise<ITaskCustomView | undefined>;
  shareView: (viewKey: string, isShared: boolean) => Promise<ITaskCustomView | undefined>;
  deleteView: (viewKey: string) => Promise<void>;
  // Local view-creating-* drafts, never sent to the API until createView.
  addDraftView: (view: ITaskCustomView) => void;
  removeDraftView: (viewKey: string) => void;
  setSelectedTaskViewKey: React.Dispatch<React.SetStateAction<string | undefined>>;
  replaceViewOrder: (viewOrder: string[]) => void;
  pruneLayoutKeys: (templateKeys: string[], customKeys: string[], sidebarKeys: string[]) => void;
  showViewInTabBar: (viewKey: string) => void;
  showCreatedCustomViewInTabBar: (viewKey: string, previousViewKey?: string) => void;
  hideViewEverywhere: (viewKey: string, isTemplateView: boolean) => void;
  hideManyViewsEverywhere: (templateViewKeys: string[], customViewKeys: string[]) => void;
  setViewSidebarVisibility: (viewKey: string, showInSidebar: boolean, previousViewKey?: string) => void;
  setManyViewsSidebarVisibility: (viewKeys: string[], showInSidebar: boolean) => void;
  removeViewFromLayout: (viewKey: string) => void;
  resetTaskViewLayout: (layout: ITaskViewLayoutState, selectedViewKey: string | undefined) => Promise<boolean>;
  setViewMemberFilter: (viewKey: string, filter: string[] | undefined) => void;
  setStandardViewFilterOverride: (viewKey: string, filter: string[] | undefined) => void;
  loadTaskViews: (refresh?: boolean, reason?: string) => Promise<void>;
  loadTaskViewLayout: (reason?: string) => Promise<void>;
}

export const TaskViewStateContext = createContext<ITaskViewStateContextValue | undefined>(undefined);

export const useTaskViewState = (): ITaskViewStateContextValue => {
  const context = useContext(TaskViewStateContext);
  if (!context) {
    throw new Error('useTaskViewState must be used within a TaskViewStateProvider');
  }

  return context;
};
